/** @jsxImportSource @emotion/react */
import {
  Button,
  Container,
  Control,
  Frequency,
  Grid,
  Icon,
  Input,
  RadioGroup,
  Select,
  Stack,
  Text,
  useBasisFormContext,
  useWatch,
  VisuallyHidden,
} from "basis";
import { Separator } from "components/Separator/Separator";
import { yesNoOptions } from "library/sharedOptions";
import { ChangeEventHandler } from "react";
import { LiabilitiesState, Liability } from "../reducer/types";
import {
  liabilityTypeOptions,
  validateMinAmount,
  validateMinFrequency,
} from "./liabilityForm";

interface Props {
  value: Liability;
  index: number;
  onRemove: (index: number) => void;
  control: Control<LiabilitiesState>;
}

export const LiabilityBox = ({ value, index, onRemove, control }: Props) => {
  const { Field, methods } = useBasisFormContext<LiabilitiesState>();

  const { setValue } = methods;

  const liabilityType = useWatch({
    control,
    name: `liabilities.${index}.liabilityType`,
  });

  const onLiabilityTypeChange: ChangeEventHandler<HTMLSelectElement> = (
    event,
  ) => {
    const { value: updatedLiabilityType } = event.target;
    if (updatedLiabilityType === "credit card") {
      setValue(`liabilities.${index}.minimumRepayment`, {
        amount: "",
        frequency: "monthly",
      });
    } else {
      setValue(`liabilities.${index}.minimumRepayment`, undefined);
    }
  };

  return (
    <Container padding="5" hasBorder>
      <Stack gap="4" width="100%" margin="0 0 4 0">
        <div
          css={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Text as="h3" textStyle="subtitle2">
            <strong>{`Liability ${index + 1}`}</strong>
          </Text>
          {index > 0 && (
            <Button variant="icon" onClick={onRemove}>
              <Icon name="trash" />
              <VisuallyHidden>
                <span>Remove liability {index + 1}</span>
              </VisuallyHidden>
            </Button>
          )}
        </div>
      </Stack>
      <Separator />

      <Container margin="4 0 0 0">
        <Grid cols="8" colsGap-sm="4" rowsGap="8">
          <Grid.Item colSpan="0-3" rowSpan="0">
            <Field
              name={`liabilities.${index}.liabilityType` as const}
              label="Type"
              options={liabilityTypeOptions}
              defaultValue={value.liabilityType}
              onChange={onLiabilityTypeChange}
              as={Select}
            />
          </Grid.Item>
          <Grid.Item colSpan="4-8" rowSpan="0">
            <Field
              name={`liabilities.${index}.financialInstitution` as const}
              label="Financial institution"
              defaultValue={value.financialInstitution}
              as={Input}
            />
          </Grid.Item>
          <Grid.Item colSpan="0-3" rowSpan="1">
            <Field
              name={`liabilities.${index}.balanceOwing` as const}
              variant="numeric"
              label="Balance owing"
              validate={validateMinAmount}
              defaultValue={value.balanceOwing}
              prefix="$"
              as={Input}
            />
          </Grid.Item>
          {liabilityType === "credit card" && (
            <Grid.Item colSpan="4-8" rowSpan="1">
              <Field
                name={`liabilities.${index}.creditLimit` as const}
                variant="numeric"
                label="Credit limit"
                defaultValue={value.creditLimit}
                validate={validateMinAmount}
                prefix="$"
                as={Input}
              />
            </Grid.Item>
          )}
          <Grid.Item colSpan="0-6" rowSpan="2">
            <Stack gap="8">
              {liabilityType !== "credit card" && (
                <Field
                  name={`liabilities.${index}.minimumRepayment` as const}
                  label="Minimum payment amount"
                  mode="select"
                  validate={validateMinFrequency}
                  defaultValue={value.minimumRepayment}
                  amountPrefix="$"
                  as={Frequency}
                />
              )}
              <Field
                name={`liabilities.${index}.hadAppliedForAssistance` as const}
                label="Have you also applied for hardship assistance for this liability?"
                columns={2}
                options={yesNoOptions}
                defaultValue={value.hadAppliedForAssistance}
                as={RadioGroup}
              />
            </Stack>
          </Grid.Item>
        </Grid>
      </Container>
    </Container>
  );
};
